import { IPauseAdProvider } from '../interfaces/IPauseAdProvider';
import { IPauseAdRequestParams } from '../interfaces/IPauseAdRequestParams';
import { IPauseAdResult } from '../interfaces/IPauseAdResult';

/**
 * @copyright © Paramount Streaming
 * @description FreeWheel pause ad provider.
 *
 * Implements `IPauseAdProvider` for the FreeWheel IMA VAST 4 endpoint.
 *
 * **URL construction** (`buildUrl`):
 * Assembles three param groups (global, merged, slot) separated by `;`
 * onto the IMA host, producing URLs like:
 * ```
 * https://7f077.v.fwmrm.net/ad/g/1?nw=169843&prof=...;csid=...&caid=...;slid=Pause01&ptgt=a
 * ```
 *
 * **Response parsing** (`parseResponse`):
 * Extracts pause ad creative data from VAST 4 XML:
 * - `MediaFile[type*="image/"]` for the image URL
 * - `ClickThrough` for the click destination
 * - `ClickTracking` elements for click tracking pixels
 * - `Impression` elements for impression tracking pixels
 */
export class FreewheelPauseAdProvider implements IPauseAdProvider {

	/**
	 * Prefix applied to client-side IMA ad call keys.
	 * Stripped before serialization so the FreeWheel endpoint receives clean parameter names.
	 */
	static readonly #IMAFW_PREFIX = 'imafw_';

	/**
	 * Mobile-specific FMS parameter keys that are excluded from desktop/web pause ad calls.
	 * These originate from the shared FreeWheel model and are irrelevant for pause ad requests.
	 */
	static readonly #EXCLUDED_KEYS: ReadonlyArray<string> = [
		'description_url',
		'_fw_continuous_play',
		'_fw_h_referer',
		'is_lat',
		'sz',
		'cpPre',
		'cpSession',
		'dai-excl',
		'api-key',
		'rdid',
		'idtype'
	];

	/** @inheritdoc */
	readonly id = 'freewheel';

	/**
	 * @description Strips the `imafw_` prefix from a key when present.
	 * Smart Tag's FreeWheel model prefixes keys for client-side ad calls;
	 * the FreeWheel endpoint expects the unprefixed form.
	 */
	#cleanKey( key: string ): string {
		return key.startsWith( FreewheelPauseAdProvider.#IMAFW_PREFIX )
			? key.substring( FreewheelPauseAdProvider.#IMAFW_PREFIX.length )
			: key;
	}

	/**
	 * @description Returns `true` if the key (or its unprefixed form) appears in the exclusion list.
	 */
	#isExcluded( key: string ): boolean {
		return FreewheelPauseAdProvider.#EXCLUDED_KEYS.includes( key )
			|| FreewheelPauseAdProvider.#EXCLUDED_KEYS.includes( key.substring( FreewheelPauseAdProvider.#IMAFW_PREFIX.length ) );
	}

	/**
	 * @description Encodes a single value for the FreeWheel query string.
	 * The literal `[RANDOM]` token is preserved unencoded -- FreeWheel replaces
	 * it server-side with a cache-busting value.
	 */
	#encode( value: unknown ): string {
		if ( value == null ) {
			return '';
		}
		const str = String( value );
		return str === '[RANDOM]' ? str : encodeURIComponent( str );
	}

	/**
	 * @description Serializes one parameter group into `key=value&key=value` format.
	 * Filters empty values, excluded keys, and de-duplicates after prefix stripping.
	 */
	#toQueryString( params: Record<string, unknown> ): string {
		if ( !params ) {
			return '';
		}

		return this.#collectEntries( params )
			.reduce<string[]>( ( acc, [ key, value ] ) => acc.concat( this.#serializeValue( key, value ) ), [] )
			.join( '&' );
	}

	/**
	 * @description Filters, deduplicates, and cleans parameter entries for serialization.
	 */
	#collectEntries( params: Record<string, unknown> ): [ string, unknown ][] {
		const seen = new Set<string>();

		return Object.entries( params )
			.filter( ( [ rawKey, value ] ) => !this.#isExcluded( rawKey ) && value != null && value !== '' )
			.reduce<[ string, unknown ][]>( ( result, [ rawKey, value ] ) => {
				const key = this.#cleanKey( rawKey );
				if ( !seen.has( key ) ) {
					seen.add( key );
					result.push( [ key, value ] );
				}
				return result;
			}, [] );
	}

	/**
	 * @description Encodes a single key-value pair (or array of values) into `key=value` query fragments.
	 */
	#serializeValue( key: string, value: unknown ): string[] {
		if ( Array.isArray( value ) ) {
			return value
				.filter( ( item ) => item != null && item !== '' )
				.map( ( item ) => `${key}=${this.#encode( item )}` );
		}
		return [ `${key}=${this.#encode( value )}` ];
	}

	/**
	 * @description Builds the FreeWheel IMA VAST 4 ad call URL from structured param groups.
	 *
	 * Param groups are separated by `;` per FreeWheel IMA convention:
	 * `{host}?{global}&...;{merged}&...;{slot}&...`
	 *
	 * @param params - Structured parameter groups from `FreeWheelPauseAdModel`.
	 * @returns Fully qualified FreeWheel IMA URL.
	 */
	buildUrl( params: IPauseAdRequestParams ): string {
		const host = 'https://7f077.v.fwmrm.net/ad/g/1';
		const groups = [
			this.#toQueryString( params?.global as Record<string, unknown> ),
			this.#toQueryString( params?.merged as Record<string, unknown> ),
			this.#toQueryString( params?.slot as Record<string, unknown> )
		].filter( Boolean );

		return `${host}?${groups.join( ';' )}`;
	}

	/**
	 * @description Parses a FreeWheel VAST 4 XML response into a normalized `IPauseAdResult`.
	 *
	 * Looks for the first `<Creative>` element containing:
	 * - `<MediaFile type="image/...">` -- pause ad image URL
	 * - `<ClickThrough>` -- click destination
	 * - `<ClickTracking>` -- click tracking pixel(s)
	 *
	 * Case-insensitive fallback selectors handle non-standard capitalization
	 * that some ad servers return.
	 *
	 * @param xml - Parsed VAST XML Document.
	 * @returns Normalized pause ad result.
	 */
	parseResponse( xml: Document ): IPauseAdResult {
		let imageSource: string | null = null;
		let clickThrough: string | null = null;
		const clickTrackingURLs: string[] = [];

		const rootNode =
			xml.querySelector( 'Creative' )
			??
			xml.querySelector( 'creative' );

		if ( rootNode ) {
			const image =
				rootNode.querySelector( 'MediaFile[type*="image/"]' )
				??
				rootNode.querySelector( 'mediafile[type*="image/"]' );

			imageSource = image?.textContent?.trim() || null;

			const clickURL =
				rootNode.querySelector( 'ClickThrough' )
				??
				rootNode.querySelector( 'clickthrough' );

			clickThrough = clickURL?.textContent?.trim() || null;

			const trackingNodes = rootNode.querySelectorAll( 'ClickTracking' );
			trackingNodes.forEach( ( node ) => {
				const content = node?.firstChild?.textContent?.trim();
				if ( content ) {
					clickTrackingURLs.push( content );
				}
			} );
		}

		return {
			imageSource,
			clickThrough,
			clickTrackingURLs,
			success: !!imageSource,
			impressions: FreewheelPauseAdProvider.#extractImpressions( xml )
		};
	}

	/**
	 * @description Collects all `<Impression>` tracking URLs from the VAST document.
	 * These are fired by the Skin when the pause ad is displayed to the user.
	 */
	static #extractImpressions( xml: Document ): string[] {
		const urls: string[] = [];
		const nodes = xml.querySelectorAll( 'Impression' );
		nodes.forEach( ( node ) => {
			const content = node?.firstChild?.textContent?.trim();
			if ( content ) {
				urls.push( content );
			}
		} );
		return urls;
	}
}